import type { AuthProvider, LinkOutcome } from '../game/auth';
import type { LeaderboardEntry, PlayerState } from '../game/types';

/**
 * Wire contract shared by the client and the Pages Functions, so a change to a
 * request shape breaks the typecheck on both sides at once.
 */

export interface CreatePlayerRequest {
  displayName?: string;
}

export interface CreatePlayerResponse {
  /**
   * Server-issued, 128-bit random. Doubles as the bearer credential for
   * /api/sync, which is why the client does not get to choose it - a
   * client-chosen id could be used to claim somebody else's row.
   */
  playerId: string;
  /** Shown once, then only ever stored locally. The server keeps a hash. */
  recoveryCode: string;
}

export interface SyncRequest {
  playerId: string;
  version: number;
  payload: PlayerState;
  /**
   * Claims for the trip currently being played, pushed alongside the lifetime
   * snapshot so a shared leaderboard costs no extra requests.
   */
  activeTrip?: {
    tripId: string;
    claims: Record<string, number>;
    version: number;
  };
  /**
   * Trips this player owns and has finished. Ended state rides along here
   * rather than on its own endpoint, so finishing a trip costs no extra
   * request. The server checks ownership itself; a joiner ending their own
   * copy must not end the trip for the car.
   */
  endedTrips?: string[];
}

export interface SyncResponse {
  /** True if our version won and the server took our payload. */
  applied: boolean;
  /** Present when the server held something newer, for the client to merge. */
  state?: PlayerState;
  serverVersion: number;
  /** Standings for the active trip, piggybacked to avoid a separate poll. */
  leaderboard?: LeaderboardEntry[];
  /**
   * This player's friends board, themselves included, by lifetime score. Sent
   * on every sync - empty when they have no friends - so a friendship removed
   * from the other side clears here too, rather than lingering.
   */
  friends?: LeaderboardEntry[];
}

export interface CreateTripRequest {
  playerId: string;
  name: string;
  /** The client's proposed code. Taken if free, otherwise the server picks one. */
  joinCode: string;
}

export interface CreateTripResponse {
  tripId: string;
  /** Authoritative - may differ from the proposal if that code was taken. */
  joinCode: string;
  name: string;
}

export interface JoinTripRequest {
  playerId: string;
  displayName: string;
  joinCode: string;
}

export interface JoinTripResponse {
  tripId: string;
  joinCode: string;
  name: string;
  startedAt: number;
  leaderboard: LeaderboardEntry[];
}

export interface FriendCodeRequest {
  playerId: string;
}

export interface FriendCodeResponse {
  /** Permanent. Minted on the first request and the same on every one after. */
  friendCode: string;
}

export interface AddFriendRequest {
  playerId: string;
  friendCode: string;
}

export interface RemoveFriendRequest {
  playerId: string;
  /** The friend's public id - the only id a client ever sees for anyone else. */
  publicId: string;
}

/** Both friend changes answer with the board as it now stands. */
export interface FriendsResponse {
  friends: LeaderboardEntry[];
}

export interface RestoreRequest {
  recoveryCode: string;
}

export interface RestoreResponse {
  playerId: string;
  payload: PlayerState;
}

/**
 * Redeems the one-time ticket the auth callback leaves in the redirect
 * fragment. The ticket is spent on first use, so this is not retryable.
 *
 * Note there is no email here or in the response: the signed-in address arrives
 * alongside the ticket in the fragment and stays on the device. Never storing it
 * server-side is the point - see functions/api/auth/[provider]/callback.ts.
 */
export interface AuthExchangeRequest {
  ticket: string;
}

export interface AuthExchangeResponse {
  outcome: LinkOutcome;
  provider: AuthProvider;
  /**
   * Who this device should be from now on. Equal to the device's existing
   * player for `linked` and `conflict`; the account's player for `adopted`.
   */
  playerId: string;
  /**
   * Present only for `adopted`, and only if that player has ever synced: the
   * account's saved collection, for the client to merge with or switch to.
   */
  payload?: PlayerState;
}

export interface AuthUnlinkRequest {
  playerId: string;
}

export interface ApiErrorBody {
  error: string;
}

export const API = {
  player: '/api/player',
  sync: '/api/sync',
  restore: '/api/restore',
  trips: '/api/trips',
  joinTrip: '/api/trips/join',
  friendCode: '/api/friends/code',
  addFriend: '/api/friends/add',
  removeFriend: '/api/friends/remove',
  authExchange: '/api/auth/exchange',
  authUnlink: '/api/auth/unlink',
  /**
   * A top-level navigation rather than a fetch - the browser has to leave the
   * app for the provider's consent screen. The player id says who this device
   * is before signing in; it is already the bearer credential everywhere else.
   */
  authStart: (provider: AuthProvider, playerId: string) =>
    `/api/auth/${provider}/start?playerId=${encodeURIComponent(playerId)}`,
} as const;
